<template>
    <v-app>
        <v-content>
            <v-container class="form-container">
                <div class="form-header">
                    <a class="link" href="/dashboard">Личный кабинет</a>
                    <!-- Заголовок -->
                    <h1 class="form-title" v-if="productType === 'cards' || cardType === 'debit' || cardType === 'credit'">Заявка на банковскую карту</h1>
                    <h1 class="form-title" v-else-if="productType === 'microloans'">Заявка на микрозайм</h1>
                    <h1 class="form-title" v-else-if="productType === 'mortgage'">Заявка на получение ипотеки</h1>
                </div>

                <!-- Основная форма -->
                <v-form ref="form" @submit.prevent="validateAndSubmit" @keydown.enter.prevent class="form">
                    <div class="form-content">
                        <v-layout row wrap class="form-row">
                            <!-- Первая строка -->
                            <v-flex xs12 sm4 class="form-group">
                                <osago-name
                                    v-model="surname"
                                    label="Фамилия"
                                    class="input-field"
                                    :rules="[rules.required, rules.cyrillic]"
                                    :is-disabled="!isEditable"
                                />
                            </v-flex>
                            <v-flex xs12 sm4 class="form-group">
                                <osago-name
                                    v-model="name"
                                    label="Имя"
                                    class="input-field"
                                    :rules="[rules.required, rules.cyrillic]"
                                    :is-disabled="!isEditable"
                                />
                            </v-flex>
                            <v-flex xs12 sm4 class="form-group">
                                <div class="patronymic-container">
                                    <osago-name
                                        v-model="patronymic"
                                        label="Отчество"
                                        :is-disabled="noPatronymic || !isEditable"
                                        class="input-field patronymic-input"
                                        :rules="noPatronymic ? [] : [rules.cyrillic]"
                                        @input="handlePatronymicInput"
                                    />
                                    <v-checkbox
                                        v-model="noPatronymic"
                                        label="Без отчества"
                                        class="checkbox-no-patronymic"
                                        hide-details
                                        :disabled="!isEditable"
                                        @change="handlePatronymicCheckbox"
                                    />
                                </div>
                            </v-flex>
                        </v-layout>
                        <!-- Вторая строка -->
                        <v-layout row wrap class="form-row">
                            <v-flex xs12 sm4 class="form-group">
                                <v-text-field
                                    v-model="phone"
                                    :rules="[rules.required, rules.phone]"
                                    label="Номер телефона"
                                    mask="+7 ### ### ## ##"
                                    validate-on-blur
                                    required
                                    clearable
                                    class="input-field"
                                    :disabled="!isEditable"
                                />
                            </v-flex>

                            <v-flex xs12 sm4 class="form-group" v-if="productType === 'cards' || cardType === 'debit' || cardType === 'credit'">
                                <v-select
                                    v-model="cardType"
                                    label="Тип продукта"
                                    :items="cardTypes"
                                    :rules="[rules.required]"
                                    item-value="val"
                                    item-text="name"
                                    class="input-field"
                                    :disabled="!isEditable"
                                />
                            </v-flex>

                            <v-flex xs12 sm4 class="form-group">
                                <osago-date
                                    v-model="birthDate"
                                    :rules="[rules.required]"
                                    label="Дата рождения"
                                    :max="dateClientMinimal"
                                    class="input-field date-input"
                                    :is-disabled="!isEditable"
                                />
                            </v-flex>
                            <v-flex xs12 sm4 class="copy-link-group" v-if="isEditable">
                                <button
                                    type="button"
                                    @click="resetForm"
                                    color="secondary"
                                    class="copy-btn"
                                >
                                    Очистить форму
                            </button>
                            </v-flex>
                        </v-layout>
                        <!-- Кнопки -->
                        <v-checkbox
                            v-if="isEditable"
                            v-model="dataConfirmed"
                            :rules="[rules.required]"
                            label="Подтверждаю достоверность введенных данных"
                            hide-details
                            :disabled="!isEditable"
                            class="confirmation-checkbox"
                        />
                        <v-layout row wrap class="actions-row" v-if="isEditable">
                            <v-flex xs12 md6 class="action-group">
                                <v-btn
                                    type="submit"
                                    color="primary"
                                    :loading="isLoading"
                                    block
                                    large
                                    class="submit-btn"
                                    :disabled="!isEditable"
                                >
                                    Отправить заявку на расчет
                                </v-btn>
                            </v-flex>
                            <v-flex xs12 md6 class="policy-group">
                                <div class="policy-text">
                                    Отправляя анкету, соглашаюсь
                                    <br>с <a target="_blank" href="https://getpolis.ru/osago_policy">политикой
                                    обработки персональных данных</a>
                                    <br>и даю
                                    <a target="_blank" href="https://getpolis.ru/soglasie_pdn">
                                        Согласие на обработку персональных данных
                                    </a>
                                </div>
                            </v-flex>
                        </v-layout>
                    </div>
                </v-form>

                <!-- Список продуктов -->
                <banking-offers-list
                    :products="products"
                    @select="selectProduct"
                    :is-editable="isEditable"
                    :show-commission="user && (user.show_commission_bp || user.user_type === 'admin')"
                />

                <!-- Модальное окно -->
                <banking-offer-preview
                    v-if="isShowModal && selectedProduct"
                    :prepared-form-data="preparedFormData"
                    :product="selectedProduct"
                    :offer-link="offerLink"
                    @calculate="calculateAndUpdate($event)"
                    @close-modal="isShowModal = false"
                />
            </v-container>
        </v-content>
    </v-app>
</template>

<script>
import moment from "moment";

export default {
    name: 'BankingOffersForm',
    props: ['order', 'data', 'user'],
    data: () => ({
        products: null,

        userCommissionPercentage: null,
        dataConfirmed: false,

        encodedData: null,

        showCopyButton: false,
        calculationLink: '',

        isShowModal: false,
        preparedFormData: null,
        selectedProduct: null,

        updatedOrder: null,

        selectedProductId: null,

        orders: null,
        orderTest: null,

        offerLink: null,

        name: '',
        surname: '',
        patronymic: '',
        noPatronymic: false,
        phone: '+7',
        birthDate: null,

        cardType: '',

        isLoading: false,
        dateClientMinimal: moment().subtract(18, 'years').format('YYYY-MM-DD'),
        rules: {
            required: v => !!v || 'Обязательное поле',
            phone: v => {
                const cleaned = v.replace(/\D/g, '');
                return cleaned.length === 11 || 'Неверный формат телефона';
            },
            cyrillic: v => /^[А-ЯЁа-яё\s-]+$/i.test(v) || 'Только кириллица, пробелы и тире'
        },
        cardTypes: [
            { name: 'Дебетовая карта', val: 'debit' },
            { name: 'Кредитная карта', val: 'credit' },
            /*{ name: 'МикроКредиты', val: 'microcredit' },*/
        ],
        productType: '',
    }),
    computed: {
        formData() {
            return {
                name: this.name,
                surname: this.surname,
                patronymic: this.patronymic,
                noPatronymic: this.noPatronymic,
                phone: this.phone,
                birthDate: this.birthDate,
                cardType: this.cardType,
            };
        },
        isEditable() {
            const path = window.location.pathname.toLowerCase();
            return path.includes('edit') || path.includes('new') || path.includes('copy');
        },
    },
    methods: {

        handlePatronymicInput(value) {
            // При вводе текста снимаем флаг "Без отчества"
            if (value.trim() !== '' && this.noPatronymic) {
                this.noPatronymic = false;
            }
        },

        handlePatronymicCheckbox(checked) {
            // При установке флага очищаем поле
            if (checked) {
                this.patronymic = '';
            }
        },
        resetForm() {
            this.name = '';
            this.surname = '';
            this.patronymic = '';
            this.noPatronymic = false;
            this.phone = '+7';
            this.birthDate = null;
            if (this.productType === 'cards' || this.cardType === 'debit' || this.cardType === 'credit') {
                this.cardType = 'debit';
            } else if (this.productType === 'microloans') {
                this.cardType = 'microcredit';
            } else if (this.productType === 'mortgage') {
                this.cardType = 'mortgage';
            }

            this.dataConfirmed = false;

            this.$refs.form.resetValidation();
        },
        parseValidationError(error) {
            if (!error.response) return 'Нет соединения с сервером';

            if (error.response.status === 422 && error.response.data.errors) {
                return Object.values(error.response.data.errors)
                    .flat()
                    .join('\n');
            }

            return error.response.data.message || 'Ошибка сервера';
        },
        selectProduct(product) {
            const path = window.location.pathname.toLowerCase();

            if(path.includes('copy') ) {
                    this.selectedProduct = {
                    ...product,
                    commission: this.updatedOrder.calculated_commissions[String(product.id)]
                };
            } else {
                    this.selectedProduct = {
                    ...product,
                    commission: this.encodedData.calculated_commissions[String(product.id)]
                };
            }

            this.isShowModal = true;
        },
        formatPhoneInput(phone) {
            // Оставляем только цифры
            const cleaned = phone.replace(/\D/g, '');
            // Обрезаем до 11 символов и добавляем +7
            return '+7' + cleaned.slice(1, 11);
        },
        isFormDataValid() {
            const cyrillicRegex = /^[А-ЯЁа-яё\s-]+$/i; // Добавлен \s для пробелов
            return (
                cyrillicRegex.test(this.name.trim())
                && cyrillicRegex.test(this.surname.trim())
                && (!this.patronymic || cyrillicRegex.test(this.patronymic.trim()))
                && this.birthDate
                && this.cardType
            );
        },
        generateCalculationLink() {
            const baseUrl = window.location.origin;
            const path = `/bp/order/${this.encodedData.uuid}`;
            this.calculationLink = baseUrl + path;
            this.showCopyButton = true;

            // Для iOS устройств добавляем специальную обработку
            if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                this.$nextTick(() => {
                    const input = document.createElement('input');
                    input.setAttribute('value', this.calculationLink);
                    document.body.appendChild(input);
                    input.select();
                    document.body.removeChild(input);
                });
            }
        },

        prepareFormData() {
            if (!this.isFormDataValid()) {
                throw new Error('Некорректные данные формы');
            }
            this.preparedFormData = {
                name: this.name.trim(),
                surname: this.surname.trim(),
                patronymic: this.noPatronymic ? '' : this.patronymic.trim(),
                phone: this.phone.replace(/\D/g, '').slice(0, 11),
                cardType: this.cardType,
                birthDate: moment(this.birthDate).format('YYYY-MM-DD'),
            };
        },
        async getProducts() {
            const path = window.location.pathname.toLowerCase();

            try {
                // Если есть выбранный продукт в существующем ордере
                if (!path.includes('edit') && !path.includes('copy') && this.selectedProductId) {

                    const response = await axios.get(`/bp/products/${this.selectedProductId}`);
                    let order;

                    if (this.isEditable) {
                        order = await this.updateOrder(response.data.form_data);
                        this.products = [{
                            ...response.data,
                            commission: order.calculated_commissions[String(response.data.id)] // явное преобразование ID в строку
                        }];
                    } else {
                        order = await axios.get(`/bp/orders/${this.encodedData.uuid}/commissions`);
                        this.products = [{
                            ...response.data,
                            commission: order.data[String(response.data.id)] // явное преобразование ID в строку
                        }];
                    }
                } else {
                    // Для новых расчетов
                    this.prepareFormData();
                    const updOrder = await this.updateOrder(this.preparedFormData);
                    this.updatedOrder = updOrder;
                    // Используем обновленные данные
                    this.products = (await axios.get('/bp/products')).data
                        .filter(p => p.type === this.cardType)
                        .map(el => ({
                            ...el,
                            commission: updOrder.calculated_commissions[String(el.id)]
                        }));


                    this.generateCalculationLink();
                    PNotify.success({
                    title: 'Успешно',
                    text: 'Продукты успешно загружены'
                    });
                }
            } catch (error) {
                console.error(error);
                PNotify.error({
                    title: 'Ошибка',
                    text: 'Не удалось загрузить продукты'
                });
            }
        },
        async validateAndSubmit() {
            // 1. Проверяем валидацию формы
            if (!this.$refs.form.validate()) {
                PNotify.error('Заполните все обязательные поля');
                return;
            }

            // 2. Проверяем подтверждение данных
            if (!this.dataConfirmed) {
                PNotify.error('Необходимо подтвердить достоверность данных');
                return;
            }

            // 3. Проверяем кастомную валидацию данных
            if (!this.isFormDataValid()) {
                PNotify.error('Проверьте правильность введенных данных');
                return;
            }

            // 4. Если все проверки пройдены
            await this.getProducts();
        },


        async calculate(id) {
            try {
                const response = await axios.get(`/bp/${this.encodedData.uuid}/process/${id}`);
                this.offerLink = response.data.link;
            } catch (error) {
                console.error(error);
            }
        },
        async updateOrder(formData) {
            const payload = JSON.stringify(formData);
            try {
                const response = await axios.put(`/bp/orders/${this.encodedData.uuid}`, {form_data: payload});
                PNotify.success('Данные успешно обновлены');
                return response.data;

            } catch (error) {
                const errorMessage = this.parseValidationError(error);
                PNotify.error({
                    title: 'Ошибка обновления',
                    text: errorMessage
                });
                console.error('Update error:', error.response.data || error.message);
                throw error;
            }
        },
        async calculateAndUpdate(productId) {
            try {
                // Добавлен вызов updateOrder с текущими данными
                await this.updateOrder(this.preparedFormData);
                await this.calculate(productId);

            } catch (error) {
                console.error(error);
            }
        },
    },

    created() {
        try {
            const path = window.location.pathname.toLowerCase();

            if (!path.includes('order') && !path.includes('edit') && !path.includes('copy')) {
                const urlParams = new URLSearchParams(window.location.search);
                let productTypeParam = urlParams.get('productType');
                this.productType = productTypeParam;

                if (!productTypeParam) {
                    productTypeParam = 'cards';
                    this.productType = productTypeParam;
                    urlParams.set('productType', productTypeParam);
                    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
                    window.history.replaceState({}, '', newUrl);
                }

                if (this.productType === 'cards') {
                    this.cardType = 'debit';
                } else if (this.productType === 'microloans') {
                    this.cardType = 'microcredit';
                } else if (this.productType === 'mortgage') {
                    this.cardType = 'mortgage';
                }

                if (this.user && this.user.phone) {
                    this.phone = this.user.phone ? this.user.phone : '+7';
                }
            }

            if (!this.data) throw new Error('Missing data prop');

            const decodedData = window.atob(this.data);
            const parsedData = JSON.parse(decodedData);

            this.encodedData = path.includes('order')
                ? parsedData.original
                : parsedData;

            this.selectedProductId = this.encodedData.selected_product_id;

            if (path.includes('order') && this.selectedProductId && !path.includes('copy')) {
                this.getProducts();
            }
            if (path.includes('order') && this.encodedData.form_data) {
                const formData = JSON.parse(this.encodedData.form_data);

                this.name = formData.name || '';
                this.surname = formData.surname || '';
                this.patronymic = formData.patronymic || '';
                this.phone = formData.phone ? this.formatPhoneInput(formData.phone) : '+7';
                if (!formData.phone) {
                    this.phone = this.user.phone ? this.user.phone : '+7';
                }
                this.birthDate = formData.birthDate;
                this.cardType = formData.cardType ? formData.cardType : 'debit';
                this.noPatronymic = formData.noPatronymic || this.patronymic === '';

                this.generateCalculationLink();
            }
        } catch (e) {
            console.error('Data parsing error:', e);
            this.encodedData = {};
            PNotify.error({
                title: 'Ошибка загрузки данных',
                text: 'Некорректный формат входных данных'
            });
        }
    },
}
</script>

<style scoped>

/deep/ input[type="checkbox"] {
            min-height: auto !important;
}

/deep/ form {
    display: flex !important;
    flex-direction: column !important;
    gap: 32px !important;
}

* {
    font-family: 'SFPro';
}

.form-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.form {
  display: block !important; /* Важно для правильной работы формы */
}

.form-content {
  display: flex !important;
  flex-direction: column !important;
  gap: 32px !important; /* Работающий gap */
}

.form-container {
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding: 24px 16px;
    margin: 0 auto;
}

.form-title {
    font-size: 24px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 32px;
    padding-bottom: 16px;
}

.form-row {
    /* margin: 0 -8px 24px; */
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.form-group {
    padding: 0 8px;
    margin-bottom: 16px;
    flex: 1;
    min-width: 280px;
}

.input-field {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    height: 56px;
}

/* /deep/ .v-input__control {
        height: 100%;
    } */

.patronymic-container {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    position: relative;
}

.patronymic-input {
        flex: 1;
        min-width: 120px;
}

.checkbox-no-patronymic {
        position: absolute;
        bottom: 50px;
        flex-shrink: 0;
}

.hidden-copy-element {
    position: fixed;
    left: -9999px;
    opacity: 0;
    pointer-events: none;
}

.form-alert {
    border-radius: 8px;
    margin: 24px 0;
    font-size: 14px;
}

.actions-row {
    margin: 32px -8px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
}

.action-group {
    padding: 0 8px;
    margin-bottom: 16px;
    flex: 1;
}

.submit-btn {
    font-weight: 600;
    letter-spacing: normal;
    font-size: 22px;
    text-transform: none;
    border-radius: 8px;
    height: 70px;
    border-radius: 100px;
    box-shadow: 0 4px 6px rgba(0, 186, 87, 0.1);

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 12px rgba(0, 186, 87, 0.15);
    }
}

.copy-link-group {
    margin-top: 20px;
}

.copy-btn {
    font-size: 14px;
    margin-left: 10px;
    text-align: center;
    color:#00ba57;
    letter-spacing: normal;
    text-decoration: underline;

}

.copy-btn.v-btn--disabled {
    opacity: 0.5;
    pointer-events: none;
}

.policy-group {
    padding: 0 16px;
    flex: 1;
    min-width: 400px;
}

.policy-text {
    font-size: 13px;
    color: #718096;
    text-align: start;
}

.policy-link {
    color: #00ba57;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s;

    &:hover {
        border-color: currentColor;
    }
}

@media (max-width: 959px) {
    .form-container {
        padding: 20px 12px;
    }

    .form-title {
        font-size: 22px;
        margin-bottom: 24px;
    }

    .form-group {
        min-width: 240px;
    }

    .submit-btn {
        height: 48px;
        font-size: 15px;
    }
    .patronymic-container {
        margin-top: 40px;
    }
}

@media (max-width: 599px) {
    .form-container {
        padding: 16px 8px;
    }

    .form-title {
        font-size: 20px;
        padding-bottom: 12px;
        margin-bottom: 20px;
    }

    .form-row {
        margin-bottom: 16px;
    }

    .input-field {
        height: 48px;
    }

    .submit-btn {
        height: 44px;
        font-size: 14px;
    }

    .policy-text {
        font-size: 12px;
    }
}
</style>



// WEBPACK FOOTER //
// resources/js/components/bank-products/banking-offers/BankingOffersForm.vue